import { fmtDate } from "@/lib/utils";
import { useEffect, useState, useCallback } from "react";
import {
    Loader2, CheckCircle2, FileText, FileSignature, Receipt, ShieldCheck,
    ClipboardCheck, Lock, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoenixMark } from "@/components/PhoenixLogo";
import { supervisionTriggerStates } from "@/lib/supervisionStates";
import {
    inputCls, labelCls, EMPTY_LICENSE, EMPTY_DEGREE, Section,
    CredentialSections, validateCredentials, buildCredentialPayload,
} from "@/components/credentialing/ProviderCredentialFields";

const STEPS = [
    { key: "application", label: "Application", icon: FileText },
    { key: "contract", label: "Contract", icon: FileSignature },
    { key: "w9", label: "W-9", icon: Receipt },
    { key: "consents", label: "Consents", icon: ShieldCheck },
];

export function ProviderOnboardingGate() {
    const { fetchMe } = useAuth();
    const [state, setState] = useState(null);
    const [active, setActive] = useState(null);

    const refresh = useCallback(async () => {
        const r = await api.get("/provider/onboarding");
        setState(r.data);
        return r.data;
    }, []);

    useEffect(() => {
        refresh().then((s) => {
            // jump to the first incomplete step
            const first = STEPS.find((st) => !s.steps?.[st.key]);
            setActive(first ? first.key : "review");
        });
    }, [refresh]);

    if (!state) {
        return (
            <div className="fixed inset-0 bg-phoenix-navy z-50 flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-white" />
            </div>
        );
    }

    if (state.submitted) return <PendingApproval onRefresh={fetchMe} />;

    const stepDone = (k) => state.steps?.[k];
    const allDone = STEPS.every((s) => stepDone(s.key));

    return (
        <div className="fixed inset-0 bg-phoenix-navy/80 backdrop-blur-sm z-50 overflow-y-auto p-4">
            <div data-testid="provider-onboarding-gate" className="max-w-3xl mx-auto my-4 bg-white rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-phoenix-navy px-6 py-5 flex items-center gap-3">
                    <PhoenixMark size={36} />
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange">Provider onboarding</div>
                        <h1 className="font-display font-bold text-lg text-white">Welcome, {state.name}</h1>
                    </div>
                </div>

                {/* Stepper */}
                <div className="px-6 py-4 border-b border-border/60 flex items-center gap-1.5 flex-wrap">
                    {STEPS.map((s) => {
                        const done = stepDone(s.key);
                        const isActive = active === s.key;
                        return (
                            <button key={s.key} data-testid={`onboard-step-${s.key}`} onClick={() => setActive(s.key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    isActive ? "bg-phoenix-navy text-white"
                                    : done ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`}>
                                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                                {s.label}
                            </button>
                        );
                    })}
                    <button data-testid="onboard-step-review" onClick={() => setActive("review")}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            active === "review" ? "bg-phoenix-navy text-white" : "bg-secondary text-muted-foreground"}`}>
                        <ClipboardCheck className="w-3.5 h-3.5" /> Submit
                    </button>
                </div>

                <div className="p-6">
                    {active === "application" && <ApplicationStep providerCtx={state} onDone={async () => { await refresh(); setActive("contract"); }} />}
                    {active === "contract" && <ContractStep contract={state.contract} onDone={async () => { await refresh(); setActive("w9"); }} />}
                    {active === "w9" && <W9Step onDone={async () => { await refresh(); setActive("consents"); }} />}
                    {active === "consents" && <ConsentsStep onDone={async () => { await refresh(); setActive("review"); }} />}
                    {active === "review" && (
                        <ReviewStep state={state} allDone={allDone} onGoto={setActive}
                            onSubmitted={async () => { await refresh(); }} />
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Step 1: Application ──────────────────────────────────────────────────
function ApplicationStep({ providerCtx, onDone }) {
    const isProvider = providerCtx?.provider_type === "provider";
    const careType = providerCtx?.care_type || (isProvider ? "psych" : "therapist");
    const [form, setForm] = useState({
        phone: "", date_of_birth: "", address: "", city: "", state: "", zip_code: "",
        npi_number: "", specialty: providerCtx?.specialty || "",
        is_supervisor: !!providerCtx?.is_supervisor, supervises_facility_ids: providerCtx?.supervises_facility_ids || [],
        dea_number: "", dea_state: "", dea_expiration: "",
        controlled_substance_number: "", controlled_substance_state: "", controlled_substance_expiration: "",
        drivers_license_expiration: "",
        supervising_physician_first_name: "", supervising_physician_last_name: "",
        supervising_physician_title: "MD", supervising_physician_phone: "",
        supervising_physician_email: "", supervising_physician_npi: "", supervising_physician_dea: "",
        collaborative_agreement_expiration: "",
        board_certification_board: "", board_certification_type: "",
        board_certification_number: "", board_certification_expiration: "",
        malpractice_insurance_carrier: "", malpractice_policy_number: "", malpractice_expiration: "",
        years_of_experience: "", certifications: "", skills_narrative: "", forensic_cst_qualified: false,
        has_pending_criminal_charges: false, criminal_charges_disclosure: "",
        has_felony_history: false, felony_history_disclosure: "",
        has_license_suspension: false, license_restriction_disclosure: "",
        has_malpractice_claims: false, malpractice_claims_disclosure: "",
    });
    const [licenses, setLicenses] = useState([{ ...EMPTY_LICENSE }]);
    const [degrees, setDegrees] = useState([{ ...EMPTY_DEGREE }]);
    const [docs, setDocs] = useState({});
    const [appTpl, setAppTpl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const setLic = (i, k, v) => setLicenses((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
    const setDeg = (i, k, v) => setDegrees((ds) => ds.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
    const setDoc = (key) => (file) => setDocs((d) => ({ ...d, [key]: file }));
    const fieldMode = (k) => appTpl?.fields?.[k] || "optional";
    const showField = (k) => fieldMode(k) !== "hidden";
    const supTriggerStates = supervisionTriggerStates(providerCtx?.credentials, [form.state, form.dea_state, ...licenses.map((l) => l.state)]);
    const supMode = appTpl?.supervising_physician || "state_based";
    const showSupervising = isProvider && supMode !== "hidden";
    const supRequired = showSupervising && (supMode === "required" || supTriggerStates.length > 0);

    useEffect(() => {
        let active = true;
        api.get(`/application-template/${careType}`).then((r) => { if (active) setAppTpl(r.data); }).catch(() => { if (active) setAppTpl(null); });
        return () => { active = false; };
    }, [careType]);

    const submit = async () => {
        setError("");
        if (!form.npi_number || form.npi_number.length < 10) { setError("A valid 10-digit NPI is required."); return; }
        const validLic = licenses.filter((l) => l.state && l.license_type && l.license_number && l.expiration_date);
        if (validLic.length === 0) { setError("Add at least one complete professional license."); return; }
        const verr = validateCredentials({ form, licenses: validLic, docs, isProvider, supRequired, supTriggerStates, showField });
        if (verr) { setError(verr); return; }
        setSaving(true);
        try {
            await api.post("/provider/onboarding/application", {
                phone: form.phone, date_of_birth: form.date_of_birth || null,
                address: form.address, city: form.city, state: form.state, zip_code: form.zip_code,
                npi_number: form.npi_number, specialty: form.specialty || null,
                is_supervisor: !!form.is_supervisor,
                supervises_facility_ids: form.is_supervisor ? form.supervises_facility_ids : [],
                ...buildCredentialPayload({ form, licenses: validLic, degrees, docs, isProvider }),
            });
            onDone();
        } catch (e) {
            setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not save. Check your entries.");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-5">
            <p className="text-sm text-muted-foreground">
                Complete your provider credentialing profile. The required fields and uploads below match your provider type
                <b className="text-phoenix-navy"> ({providerCtx?.credentials || careType})</b>.
            </p>
            {error && <p data-testid="onboard-app-error" className="text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-lg px-3 py-2">{error}</p>}

            <Section title="Personal & Practice Information">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Mobile phone *</label>
                        <input data-testid="onb-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" />
                        <p className="text-[11px] text-muted-foreground mt-1">Must be a <b>mobile number</b> — used for urgent SMS paging (e.g. an on-call suicide-watch alert).</p>
                    </div>
                    <div><label className={labelCls}>Date of birth</label><input type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
                </div>
                <div className="mt-3"><label className={labelCls}>Practice address</label><input data-testid="onb-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <div><label className={labelCls}>City</label><input data-testid="onb-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                    <div><label className={labelCls}>State</label><input data-testid="onb-state" className={inputCls} value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                    <div><label className={labelCls}>ZIP</label><input data-testid="onb-zip" className={inputCls} value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <div><label className={labelCls}>NPI number *</label><input data-testid="onb-npi" className={inputCls} maxLength={10} value={form.npi_number} onChange={(e) => set("npi_number", e.target.value)} /></div>
                    <div><label className={labelCls}>Specialty</label><input className={inputCls} value={form.specialty} onChange={(e) => set("specialty", e.target.value)} /></div>
                </div>
                <label className="flex items-start gap-2.5 mt-4 p-3 rounded-lg border border-border bg-secondary/20 cursor-pointer">
                    <input data-testid="onb-supervisor" type="checkbox" className="w-4 h-4 mt-0.5 accent-phoenix-navy" checked={form.is_supervisor} onChange={(e) => set("is_supervisor", e.target.checked)} />
                    <span className="text-sm text-foreground">I am a <b>Supervisor / Medical Director</b>
                        <span className="block text-[11px] text-muted-foreground mt-0.5">Supervisors receive escalated suicide-watch alerts when the on-call provider doesn't respond.</span>
                    </span>
                </label>
                {form.is_supervisor && (
                    <div data-testid="onb-supervisor-facilities" className="mt-2 ml-7 p-3 rounded-lg border border-border bg-white">
                        <p className="text-xs font-semibold text-phoenix-navy mb-2">Facilities you supervise</p>
                        {(providerCtx?.facilities || []).length === 0 ? (
                            <p className="text-[11px] text-muted-foreground">No facilities are assigned to you yet — an administrator will confirm your facility coverage.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {providerCtx.facilities.map((f) => (
                                    <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" data-testid={`onb-supfac-${f.id}`} className="accent-phoenix-navy"
                                            checked={(form.supervises_facility_ids || []).includes(f.id)}
                                            onChange={(e) => set("supervises_facility_ids", e.target.checked
                                                ? [...new Set([...(form.supervises_facility_ids || []), f.id])]
                                                : (form.supervises_facility_ids || []).filter((x) => x !== f.id))} />
                                        {f.name}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Section>

            <CredentialSections
                form={form} set={set}
                licenses={licenses} setLic={setLic} setLicenses={setLicenses}
                degrees={degrees} setDeg={setDeg} setDegrees={setDegrees}
                docs={docs} setDoc={setDoc}
                isProvider={isProvider} appTpl={appTpl} fieldMode={fieldMode} showField={showField}
                supRequired={supRequired} supTriggerStates={supTriggerStates} showSupervising={showSupervising} />

            <button data-testid="onb-application-submit" disabled={saving} onClick={submit}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save & continue to contract"}
            </button>
        </div>
    );
}
// ── Step 2: Contract ─────────────────────────────────────────────────────
function ContractStep({ contract, onDone }) {
    const [signature, setSignature] = useState("");
    const [agree, setAgree] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    if (!contract) return <p className="text-sm text-muted-foreground">No contract found. Contact your administrator.</p>;
    if (contract.signed) {
        return (
            <div className="text-center py-8 space-y-3">
                <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
                <p className="text-sm text-foreground font-semibold">Contract signed.</p>
                <button data-testid="contract-next" onClick={onDone} className="px-4 py-2.5 bg-phoenix-navy text-white text-sm font-semibold rounded-lg">Continue to W-9</button>
            </div>
        );
    }

    const sign = async () => {
        setError(""); setSaving(true);
        try {
            await api.post(`/provider/employment/contract/${contract.id}/sign`, { signature: signature.trim() });
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not sign. Try again.");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Review your independent contractor agreement (${contract.hourly_rate}/hr · {contract.hours_per_week} hrs/wk · starts {fmtDate(contract.contract_start_date)}) and e-sign to continue.
            </p>
            <pre data-testid="onb-contract-text" className="bg-secondary/40 border border-border rounded-lg p-4 text-[11px] leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto font-mono">{contract.content}</pre>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <label className="flex items-start gap-2 text-sm text-phoenix-navy">
                <input data-testid="onb-contract-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 rounded" />
                I have read and agree to the terms of this agreement.
            </label>
            <input data-testid="onb-contract-signature" className={inputCls} placeholder="Full legal name (electronic signature)" value={signature} onChange={(e) => setSignature(e.target.value)} />
            <button data-testid="onb-contract-sign" disabled={!agree || signature.trim().length < 2 || saving} onClick={sign}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Sign & continue"}
            </button>
        </div>
    );
}

// ── Step 3: W-9 ──────────────────────────────────────────────────────────
function W9Step({ onDone }) {
    const [f, setF] = useState({ w9_legal_name: "", w9_business_name: "", w9_tin_type: "ssn", w9_tax_id: "",
        w9_address: "", w9_city_state_zip: "", w9_certification: false });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async () => {
        setError("");
        if (!f.w9_certification) { setError("You must certify the W-9 information."); return; }
        setSaving(true);
        try {
            await api.post("/provider/onboarding/w9", f);
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not save W-9.");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Form W-9 — Request for Taxpayer Identification Number and Certification.</p>
            {error && <p data-testid="onb-w9-error" className="text-sm text-destructive">{error}</p>}
            <div><label className={labelCls}>Legal name (as shown on your tax return)</label><input data-testid="onb-w9-name" className={inputCls} value={f.w9_legal_name} onChange={(e) => set("w9_legal_name", e.target.value)} /></div>
            <div><label className={labelCls}>Business name (if different)</label><input className={inputCls} value={f.w9_business_name} onChange={(e) => set("w9_business_name", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>TIN type</label>
                    <select className={inputCls} value={f.w9_tin_type} onChange={(e) => set("w9_tin_type", e.target.value)}>
                        <option value="ssn">SSN</option><option value="ein">EIN</option>
                    </select>
                </div>
                <div><label className={labelCls}>Taxpayer ID number</label><input data-testid="onb-w9-tin" className={inputCls} value={f.w9_tax_id} onChange={(e) => set("w9_tax_id", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Address</label><input data-testid="onb-w9-address" className={inputCls} value={f.w9_address} onChange={(e) => set("w9_address", e.target.value)} /></div>
            <div><label className={labelCls}>City, state, ZIP</label><input data-testid="onb-w9-csz" className={inputCls} value={f.w9_city_state_zip} onChange={(e) => set("w9_city_state_zip", e.target.value)} /></div>
            <label className="flex items-start gap-2 text-sm text-phoenix-navy">
                <input data-testid="onb-w9-cert" type="checkbox" checked={f.w9_certification} onChange={(e) => set("w9_certification", e.target.checked)} className="mt-0.5 rounded" />
                Under penalties of perjury, I certify that the information above is correct and that I am a U.S. person.
            </label>
            <button data-testid="onb-w9-submit" disabled={saving || !f.w9_certification} onClick={submit}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save W-9 & continue"}
            </button>
        </div>
    );
}

// ── Step 4: Consents ─────────────────────────────────────────────────────
function ConsentsStep({ onDone }) {
    const [data, setData] = useState(null);
    const [checked, setChecked] = useState({});
    const [signature, setSignature] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/provider/staff-consent").then((r) => {
            if (r.data.accepted) { onDone(); return; }
            setData(r.data);
        }).catch(() => setError("Could not load consents."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!data) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-phoenix-navy" /></div>;
    const items = data.items || [];
    const allChecked = items.length > 0 && items.every((i) => checked[i.id]);

    const sign = async () => {
        setError(""); setSaving(true);
        try {
            await api.post("/provider/staff-consent", {
                items: items.map((i) => ({ id: i.id, label: i.label, agreed: !!checked[i.id] })),
                signature_name: signature.trim(),
            });
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not record consent.");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Review and agree to each Care Provider consent below.</p>
            <div className="space-y-3">
                {items.map((it) => (
                    <label key={it.id} data-testid={`onb-consent-${it.id}`} className="block bg-secondary/30 border border-border rounded-lg p-4 cursor-pointer hover:border-phoenix-navy/40 transition-colors">
                        <div className="flex items-start gap-3">
                            <input type="checkbox" className="mt-1 rounded" checked={!!checked[it.id]} onChange={(e) => setChecked((c) => ({ ...c, [it.id]: e.target.checked }))} />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-phoenix-navy">{it.label}</div>
                                <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap max-h-40 overflow-y-auto">{it.detail}</div>
                            </div>
                        </div>
                    </label>
                ))}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <input data-testid="onb-consent-signature" className={inputCls} placeholder="Full legal name (electronic signature)" value={signature} onChange={(e) => setSignature(e.target.value)} />
            <button data-testid="onb-consent-submit" disabled={!allChecked || signature.trim().length < 2 || saving} onClick={sign}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "I agree — sign & continue"}
            </button>
        </div>
    );
}

// ── Step 5: Review & submit ──────────────────────────────────────────────
function ReviewStep({ state, allDone, onGoto, onSubmitted }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async () => {
        setError(""); setSaving(true);
        try {
            await api.post("/provider/onboarding/submit");
            onSubmitted();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not submit. Complete all steps first.");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4">
            <h2 className="font-display font-bold text-lg text-phoenix-navy">Review & submit</h2>
            <p className="text-sm text-muted-foreground">Confirm each step is complete, then submit for administrator approval.</p>
            <div className="space-y-2">
                {STEPS.map((s) => {
                    const done = state.steps?.[s.key];
                    return (
                        <div key={s.key} className="flex items-center justify-between bg-secondary/30 border border-border rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><s.icon className="w-4 h-4 text-muted-foreground" /> {s.label}</div>
                            {done ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><CheckCircle2 className="w-4 h-4" /> Complete</span>
                            ) : (
                                <button data-testid={`review-goto-${s.key}`} onClick={() => onGoto(s.key)} className="text-xs font-semibold text-phoenix-orange hover:underline">Complete now →</button>
                            )}
                        </div>
                    );
                })}
            </div>
            {error && <p data-testid="onb-submit-error" className="text-sm text-destructive">{error}</p>}
            <button data-testid="onboard-submit-final" disabled={!allDone || saving} onClick={submit}
                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : allDone ? "Submit for approval" : "Complete all steps to submit"}
            </button>
        </div>
    );
}

// ── Locked: pending approval ─────────────────────────────────────────────
function PendingApproval({ onRefresh }) {
    const [checking, setChecking] = useState(false);
    return (
        <div className="fixed inset-0 bg-phoenix-navy z-50 flex items-center justify-center p-4">
            <div data-testid="onboard-pending-approval" className="bg-white rounded-2xl max-w-md w-full p-8 text-center space-y-4">
                <span className="w-14 h-14 rounded-2xl bg-phoenix-soft text-phoenix-navy flex items-center justify-center mx-auto"><Lock className="w-7 h-7" /></span>
                <h1 className="font-display font-bold text-xl text-phoenix-navy">Onboarding submitted</h1>
                <p className="text-sm text-muted-foreground">
                    Thank you — your application, signed contract, W-9, and consents are complete.
                    Your account is now <span className="font-semibold text-foreground">pending administrator approval</span>.
                    You'll gain full access as soon as it's approved.
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5" /> Awaiting approval</div>
                <button data-testid="onboard-refresh-status" disabled={checking}
                    onClick={async () => { setChecking(true); await onRefresh(); setChecking(false); }}
                    className="px-4 py-2.5 bg-phoenix-navy text-white text-sm font-semibold rounded-lg disabled:opacity-50">
                    {checking ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Check approval status"}
                </button>
            </div>
        </div>
    );
}
